import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Wordmark } from "../sections/Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const ADMIN_TOKEN_KEY = "leadtochair_admin_token";

const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 font-sans text-ink">
      <div data-testid="admin-login-card" className="w-full max-w-sm">
        <div className="flex justify-center">
          <Link to="/" aria-label="Back to Leadtochair home">
            <Wordmark />
          </Link>
        </div>
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="mt-10 rounded-2xl border border-ink/10 bg-white p-8"
        >
          <h1 className="font-display text-2xl font-medium text-ink">Admin sign in</h1>
          <p className="mt-2 text-sm text-ink/55">View and manage demo requests.</p>

          <label htmlFor="admin-email-input" className="mb-2 mt-7 block text-xs font-semibold uppercase tracking-[0.14em] text-ink/60">
            Email
          </label>
          <input
            id="admin-email-input"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-teal"
          />

          <label htmlFor="admin-password-input" className="mb-2 mt-5 block text-xs font-semibold uppercase tracking-[0.14em] text-ink/60">
            Password
          </label>
          <input
            id="admin-password-input"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-teal"
          />

          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600">
              {error}
            </p>
          )}

          <button
            data-testid="admin-login-submit-btn"
            type="submit"
            disabled={loading}
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-teal px-6 py-3.5 text-sm font-medium tracking-wide text-white transition-colors hover:bg-teal-dark disabled:opacity-70"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
}
